import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getFirebaseToken } from '../services/authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:9000/api/v1';

// Create an axios instance with auth
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Add auth interceptor
api.interceptors.request.use(async (config) => {
  const token = await getFirebaseToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

interface AvailableParser {
  parser_class_name: string;
  parser_name: string;
  erp_system_name: string;
}

interface ERPSystem {
  id: number;
  erp_name: string;
  parser_class_name: string;
  is_active: boolean;
  description?: string;
  created_at: string;
}

const ERPSettings: React.FC = () => {
  const [availableParsers, setAvailableParsers] = useState<AvailableParser[]>([]);
  const [configuredERP, setConfiguredERP] = useState<ERPSystem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedParser, setSelectedParser] = useState('');
  const [showConfig, setShowConfig] = useState(false);

  useEffect(() => {
    loadERPSettings();
  }, []);

  const loadERPSettings = async () => {
    setIsLoading(true);
    try {
      // Load available parsers
      const parsersRes = await api.get('/erp-systems/available-parsers');
      setAvailableParsers(parsersRes.data.parsers || []);

      // Load configured ERP system
      const configuredRes = await api.get('/erp-systems');
      if (configuredRes.data && configuredRes.data.length > 0) {
        setConfiguredERP(configuredRes.data[0]); // Get the first configured ERP
      }
    } catch (error: any) {
      console.error('Failed to load ERP settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfigureERP = async () => {
    if (!selectedParser) {
      alert('Please select an ERP system');
      return;
    }

    setIsSaving(true);
    try {
      const parser = availableParsers.find(p => p.parser_class_name === selectedParser);
      if (!parser) {
        alert('Invalid parser selection');
        return;
      }

      // Create new ERP system configuration
      const response = await api.post('/erp-systems', {
        erp_name: parser.erp_system_name,
        parser_class_name: parser.parser_class_name,
        is_active: true,
        description: `${parser.erp_system_name} integration`
      });

      setConfiguredERP(response.data);
      setShowConfig(false);
      setSelectedParser('');
      alert('ERP system configured successfully!');
    } catch (error: any) {
      console.error('Failed to configure ERP:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to configure ERP system';
      alert(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveERP = async () => {
    if (!configuredERP) return;

    if (!window.confirm('Are you sure you want to remove this ERP configuration?')) {
      return;
    }

    try {
      await api.delete(`/erp-systems/${configuredERP.id}`);
      setConfiguredERP(null);
      alert('ERP system removed successfully');
    } catch (error: any) {
      console.error('Failed to remove ERP:', error);
      alert('Failed to remove ERP system');
    }
  };

  const handleToggleActive = async () => {
    if (!configuredERP) return;

    try {
      const endpoint = configuredERP.is_active ? 'deactivate' : 'activate';
      const response = await api.post(`/erp-systems/${configuredERP.id}/${endpoint}`);
      setConfiguredERP(response.data);
    } catch (error: any) {
      console.error('Failed to toggle ERP status:', error);
      alert('Failed to update ERP status');
    }
  };

  if (isLoading) {
    return (
      <div className="rounded-lg shadow p-6" style={{ background: 'var(--bg-secondary)' }}>
        <h2 className="text-lg font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
          ERP System Integration
        </h2>
        <div style={{ color: 'var(--text-secondary)' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div className="rounded-lg shadow p-6" style={{ background: 'var(--bg-secondary)' }}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
          ERP System Integration
        </h2>
        {!configuredERP && !showConfig && (
          <button
            onClick={() => setShowConfig(true)}
            className="px-4 py-2 rounded font-medium text-sm"
            style={{
              background: 'var(--btn-bg)',
              color: 'var(--btn-text)'
            }}
          >
            Configure ERP
          </button>
        )}
      </div>

      {/* Configured ERP Display */}
      {configuredERP && (
        <div className="space-y-4">
          <div className="p-4 rounded-lg border" style={{ borderColor: 'var(--border-color)' }}>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium" style={{ color: 'var(--text-primary)' }}>
                  {configuredERP.erp_name}
                </div>
                <div className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                  {configuredERP.description || 'No description'}
                </div>
                <div className="mt-2">
                  <span
                    className="inline-block px-2 py-1 text-xs rounded"
                    style={{
                      background: configuredERP.is_active ? '#10b981' : '#6b7280',
                      color: 'white'
                    }}
                  >
                    {configuredERP.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleToggleActive}
                  className="px-3 py-1 rounded text-sm"
                  style={{
                    background: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border-color)'
                  }}
                >
                  {configuredERP.is_active ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  onClick={handleRemoveERP}
                  className="px-3 py-1 rounded text-sm bg-red-500 text-white hover:bg-red-600"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>

          <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            <p className="mb-2">Your production reports from {configuredERP.erp_name} will be automatically parsed and imported.</p>
            <p>Upload reports manually or set up email integration to import them automatically.</p>
          </div>
        </div>
      )}

      {/* ERP Configuration Form */}
      {!configuredERP && showConfig && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
              Select Your ERP System
            </label>
            <select
              value={selectedParser}
              onChange={(e) => setSelectedParser(e.target.value)}
              className="w-full px-3 py-2 border rounded-md"
              style={{
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                borderColor: 'var(--border-color)'
              }}
            >
              <option value="">-- Select ERP System --</option>
              {availableParsers.map((parser) => (
                <option key={parser.parser_class_name} value={parser.parser_class_name}>
                  {parser.erp_system_name}
                </option>
              ))}
            </select>
            <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
              Select the ERP system you use for production management.
            </p>
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleConfigureERP}
              disabled={isSaving || !selectedParser}
              className="px-4 py-2 rounded font-medium disabled:opacity-50"
              style={{
                background: 'var(--btn-bg)',
                color: 'var(--btn-text)'
              }}
            >
              {isSaving ? 'Saving...' : 'Save Configuration'}
            </button>
            <button
              onClick={() => {
                setShowConfig(false);
                setSelectedParser('');
              }}
              className="px-4 py-2 rounded font-medium"
              style={{
                background: 'var(--bg-primary)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-color)'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* No ERP Configured */}
      {!configuredERP && !showConfig && (
        <div className="text-center py-8">
          <div className="text-4xl mb-3">📊</div>
          <p className="mb-2" style={{ color: 'var(--text-primary)' }}>
            No ERP System Configured
          </p>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Configure your ERP system to start importing production data automatically.
          </p>
        </div>
      )}
    </div>
  );
};

export default ERPSettings;
